/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { BaseApiService } from './BaseApiService';
import type { GenericAbortSignal } from 'axios';
import type {
  HotelSearchRequest,
  HotelSearchResponse,
  SmartFillResponse,
  SmartfillResponse,
  PropertyNameSmartFillRequest,
  AppEnvironment,
} from '../types';
import { splitOccupancyByRoom } from '../utils/occupancy';

export class HotelsSearchService extends BaseApiService {
  constructor(environment: AppEnvironment = 'stg') {
    super(environment, {
      stg: 'https://hotelsbe.stg-apiairasia.com',
      prd: 'https://hotelsbe.apiairasia.com',
    });
  }

  /**
   * Fetches hotel search results from the BFF v2 endpoint.
   * @param params - Search criteria including location, dates, and occupancy.
   * @param token - Bearer authentication token.
   * @param clientId - Application client ID.
   * @param accessToken - Optional user access token (passed as X-Auth).
   * @param refreshToken - Optional user refresh token (passed as refresh-token).
   */
  public searchHotels(
    params: HotelSearchRequest,
    token: string,
    clientId: string,
    accessToken?: string,
    refreshToken?: string,
    signal?: GenericAbortSignal,
  ): Promise<HotelSearchResponse> {
    // Correctly initialize URLSearchParams with single-value fields
    const queryParams = new URLSearchParams({
      currency: params.currency?.toUpperCase(),
      country_code: params.country_code?.toUpperCase(),
      checkin: params.checkin,
      checkout: params.checkout,
      location_id: params.landmark || params.location_id,
      hotels_size: String(params.hotels_size ?? 150),
      language: params.language ?? 'en-us',
      ...(params.page_number !== undefined && {
        page_number: String(params.page_number),
      }),
      ...(params.user_selected_hotel_id && {
        user_selected_hotel_id: params.user_selected_hotel_id,
      }),
      ...(params.guest_score && {
        guest_score: params.guest_score,
      }),
      ...(params.big_member_id && {
        big_member_id: params.big_member_id,
      }),
      ...(params.mType && {
        mType: params.mType,
      }),
      ...(params.debug === true && {
        debug: String(params.debug),
      }),
    });

    const channels = Array.isArray(params.channel)
      ? params.channel
      : params.channel
        ? [params.channel]
        : [];

    channels.forEach((channel) => {
      queryParams.append('channel', channel);
    });

    params.payment_method?.forEach((value) => {
      queryParams.append('payment_method', value.toLowerCase());
    });

    params.price_range?.forEach((value) => {
      queryParams.append('price_range', value);
    });

    params.promotion_type?.forEach((value) => {
      queryParams.append('promotion_type', value);
    });

    params.category?.forEach((value) => {
      queryParams.append('category', value);
    });

    // Handle array or single-value star_rating
    const starRatings = Array.isArray(params.star_rating)
      ? params.star_rating
      : params.star_rating
        ? [params.star_rating]
        : [];

    starRatings.forEach((rating) => {
      queryParams.append('star_rating', String(rating));
    });

    // Handle amenities array
    params.amenity?.forEach((item) => {
      if (item) {
        queryParams.append('amenity', item);
      }
    });

    params.rate_amenity?.forEach((item) => {
      if (item) {
        queryParams.append('rate_amenity', item);
      }
    });

    // Handle occupancy logic
    const occupancyByRoom = splitOccupancyByRoom(params.occupancy);
    if (occupancyByRoom.length === 0) {
      // Fallback if split returns empty, ensure occupancy is a string
      queryParams.append('occupancy', String(params.occupancy));
    } else {
      occupancyByRoom.forEach((roomOccupancy) => {
        queryParams.append('occupancy', roomOccupancy);
      });
    }

    const headers = {
      'client-id': clientId,
      Authorization: `Bearer ${token}`,
      ...(accessToken && { 'X-Auth': accessToken }),
      ...(refreshToken && { 'refresh-token': refreshToken }),
      'X-AS-Profile': params.asProfile ?? '0',
    };

    if (signal) {
      return this.get('/bff/search/v2/hotels', queryParams, headers, {
        signal,
      });
    }

    return this.get('/bff/search/v2/hotels', queryParams, headers);
  }

  /**
   * Fetches Smartfill location details.
   */
  public async fetchSmartfill(
    locationId: string,
    token: string,
    clientId: string,
    language: string = 'en-us',
  ): Promise<SmartfillResponse> {
    const queryParams = { language };

    return this.get(`/smartfill/v3/smartfill/${locationId}`, queryParams, {
      'Client-ID': clientId,
      Authorization: `Bearer ${token}`,
    });
  }

  public async fetchPropertyNameSuggestions(
    request: PropertyNameSmartFillRequest,
    token: string,
    clientId: string,
  ): Promise<SmartFillResponse> {
    return this.get(
      '/smartfill/v3/smartfill/hotels',
      {
        language: request.language,
        query: request.query,
        lat: String(request.lat),
        lon: String(request.lon),
      },
      {
        'client-id': clientId,
        Authorization: `Bearer ${token}`,
      },
    );
  }

  /**
   * Helper to get ONLY the name from smartfill.
   */
  public async getSmartfillName(
    locationId: string,
    token: string,
    clientId: string,
    language: string = 'en-gb',
  ): Promise<string> {
    const data = await this.fetchSmartfill(
      locationId,
      token,
      clientId,
      language,
    );
    return data?.name || '';
  }
}
